import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

/**
 * Subscribes to /ws/admin and refreshes device views when a device goes online/offline,
 * changes channel or applies a config. Reconnects with backoff.
 */
export function useDeviceLiveUpdates() {
  const qc = useQueryClient();
  useEffect(() => {
    let ws: WebSocket | null = null;
    let stopped = false;
    let retry = 1000;
    let timer: number | undefined;
    let pending: number | undefined;

    const connect = () => {
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      ws = new WebSocket(`${proto}://${window.location.host}/ws/admin`);
      ws.onopen = () => {
        retry = 1000;
      };
      ws.onmessage = (ev) => {
        const frame = JSON.parse(String(ev.data)) as { t: string };
        if (frame.t === 'device.update' && pending === undefined) {
          // Coalesce bursts (many STBs reconnecting at once) into one refetch.
          pending = window.setTimeout(() => {
            pending = undefined;
            void qc.invalidateQueries({ queryKey: ['devices'] });
            void qc.invalidateQueries({ queryKey: ['device'] });
          }, 500);
        }
      };
      ws.onclose = () => {
        if (stopped) return;
        timer = window.setTimeout(connect, retry);
        retry = Math.min(retry * 2, 30_000);
      };
    };
    connect();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      window.clearTimeout(pending);
      ws?.close();
    };
  }, [qc]);
}
